// externals
import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// store
import { vehicleDetailsSelectors, fetchVehicleDetails } from '../store';

/**
 * Triggers a vehicle details API fetch on mount.
 * Vehicle identity and dealerId are read from the store via additional selectors —
 * no params are accepted, nothing is returned.
 * For custom vehicles, uses chromeStyleId as the query param.
 * For inventory vehicles, uses vin and dealerId.
 * Dispatches fetchVehicleDetails when `getHasVehicleData` is false.
 * Re-fetches when the derived params or bffUrl change, or when `hasData`
 * flips back to false (e.g. the slice is reset on a deal refresh).
 */
export const useFetchVehicleDetails = (): void => {
    const dispatch = useDispatch();

    const hasData = useSelector(vehicleDetailsSelectors.getHasVehicleData);
    const bffUrl = useSelector(vehicleDetailsSelectors.additionalSelectors.getVehicleDetailsBffUrl);
    const vehicle = useSelector(vehicleDetailsSelectors.additionalSelectors.getVehicle);
    const dealerId = useSelector(vehicleDetailsSelectors.additionalSelectors.getCommonOrgId);

    const params = useMemo(
        () => (vehicle?.isCustom ? { chromeStyleId: vehicle.chromeStyleId } : { vin: vehicle?.vin, dealerId }),
        [vehicle?.isCustom, vehicle?.vin, vehicle?.chromeStyleId, dealerId]
    );

    useEffect(() => {
        if (!hasData) {
            dispatch(fetchVehicleDetails(params, bffUrl));
        }
    }, [params, bffUrl, hasData]);
};
